/**
 * Binary Configuration Import/Export Module
 * Spec §6.13: Bridge Runtime Image (BRI) format (NORMATIVE)
 *
 * Handles:
 * - Exporting store state to binary BRI format
 * - Importing and validating binary configurations
 * - CRC32 calculation per IEEE 802.3
 */

import type { ConfigStore } from '@/store/configStore';
import type { KeyOverride, LEDOverride, LEDLayerConfig, LEDLayerMode, Zone, AnalogConfig, SliderConfig, SliderMidiType, PedalDetectMode, ScaleType, TuningTable } from '@/types/config';
import { SCALE_MASKS } from '@/types/config';
import type { KeyAction } from '@/types/midi';
import { calculateMaxPressedBrightness } from '@/lib/powerBudget';

// §6.13.2: Magic bytes "STAK" (0x53 0x54 0x41 0x4B)
const MAGIC_BYTES = [0x53, 0x54, 0x41, 0x4B] as const;

// Version constants - spec v1.4
const VERSION_MAJOR = 1;
const VERSION_MINOR = 4;

// Header flags
const BRI_FLAG_LED_ENABLED = 0x01;
const BRI_FLAG_ANALOG_PRESENT = 0x02;
const BRI_FLAG_HAS_NAME = 0x08;        // v2.1: Config name section present (32 bytes before CRC)

const CONFIG_NAME_SIZE = 32;           // v2.1: Fixed-size config name section

// §6.13: Structure sizes (NORMATIVE) - v1.4
const HEADER_SIZE = 20;           // v1.3: Extended from 16 for zone/layer_mapping counts
const GRID_CONFIG_SIZE = 4;
const NOTE_MAPPING_SIZE = 8;
const MIDI_CONFIG_SIZE = 4;
const ANALOG_INPUTS_SIZE = 40;    // v1.4: Analog inputs config (optional)
const LED_SETTINGS_SIZE = 16;
const LED_DEFAULT_SIZE = 16;
const SCALE_CONFIG_SIZE = 40;
const LAYER_SIZE = 8;
const ZONE_SIZE = 8;              // v1.3: New zone section
const LAYER_NOTE_MAPPING_SIZE = 8; // v1.3: Per-layer note mapping
const ZONE_NOTE_MAPPING_SIZE = 8;  // v2.0: Per-zone note mapping (same layout as layer)
const OVERRIDE_SIZE = 16;         // v1.3: Extended from 8 for full chord support
const LED_OVERRIDE_SIZE = 12;
const CRC32_SIZE = 4;

// Analog inputs constants
const MAX_SLIDERS = 3;

// v2.0/v2.1 constants
const VERSION_MAJOR_V2 = 2;
const VERSION_MINOR_V2 = 1;  // v2.1: adds scale_type byte to scale section
const BRI_FLAG_TUNING_PRESENT = 0x04;
const NOTE_MAPPING_SIZE_V2 = 16;
const SCALE_CONFIG_BASE_V2 = 14;  // v2.1: was 13, +1 byte for scale_type
const ZONE_SIZE_V2 = 10;
const LAYER_NOTE_MAPPING_SIZE_V2 = 12;
const ZONE_NOTE_MAPPING_SIZE_V2 = 12; // v2.0: Per-zone note mapping (same layout as layer)

// §6.13.12: CRC32 lookup table (IEEE 802.3, polynomial 0xEDB88320)
// Pre-computed table identical to firmware for guaranteed compatibility
const CRC32_TABLE: readonly number[] = [
  0x00000000, 0x77073096, 0xEE0E612C, 0x990951BA,
  0x076DC419, 0x706AF48F, 0xE963A535, 0x9E6495A3,
  0x0EDB8832, 0x79DCB8A4, 0xE0D5E91E, 0x97D2D988,
  0x09B64C2B, 0x7EB17CBD, 0xE7B82D07, 0x90BF1D91,
  0x1DB71064, 0x6AB020F2, 0xF3B97148, 0x84BE41DE,
  0x1ADAD47D, 0x6DDDE4EB, 0xF4D4B551, 0x83D385C7,
  0x136C9856, 0x646BA8C0, 0xFD62F97A, 0x8A65C9EC,
  0x14015C4F, 0x63066CD9, 0xFA0F3D63, 0x8D080DF5,
  0x3B6E20C8, 0x4C69105E, 0xD56041E4, 0xA2677172,
  0x3C03E4D1, 0x4B04D447, 0xD20D85FD, 0xA50AB56B,
  0x35B5A8FA, 0x42B2986C, 0xDBBBBBD6, 0xACBCCB40,
  0x32D86CE3, 0x45DF5C75, 0xDCD60DCF, 0xABD13D59,
  0x26D930AC, 0x51DE003A, 0xC8D75180, 0xBFD06116,
  0x21B4F4B5, 0x56B3C423, 0xCFBA9599, 0xB8BDA50F,
  0x2802B89E, 0x5F058808, 0xC60CD9B2, 0xB10BE924,
  0x2F6F7C87, 0x58684C11, 0xC1611DAB, 0xB6662D3D,
  0x76DC4190, 0x01DB7106, 0x98D220BC, 0xEFD5102A,
  0x71B18589, 0x06B6B51F, 0x9FBFE4A5, 0xE8B8D433,
  0x7807C9A2, 0x0F00F934, 0x9609A88E, 0xE10E9818,
  0x7F6A0DBB, 0x086D3D2D, 0x91646C97, 0xE6635C01,
  0x6B6B51F4, 0x1C6C6162, 0x856530D8, 0xF262004E,
  0x6C0695ED, 0x1B01A57B, 0x8208F4C1, 0xF50FC457,
  0x65B0D9C6, 0x12B7E950, 0x8BBEB8EA, 0xFCB9887C,
  0x62DD1DDF, 0x15DA2D49, 0x8CD37CF3, 0xFBD44C65,
  0x4DB26158, 0x3AB551CE, 0xA3BC0074, 0xD4BB30E2,
  0x4ADFA541, 0x3DD895D7, 0xA4D1C46D, 0xD3D6F4FB,
  0x4369E96A, 0x346ED9FC, 0xAD678846, 0xDA60B8D0,
  0x44042D73, 0x33031DE5, 0xAA0A4C5F, 0xDD0D7CC9,
  0x5005713C, 0x270241AA, 0xBE0B1010, 0xC90C2086,
  0x5768B525, 0x206F85B3, 0xB966D409, 0xCE61E49F,
  0x5EDEF90E, 0x29D9C998, 0xB0D09822, 0xC7D7A8B4,
  0x59B33D17, 0x2EB40D81, 0xB7BD5C3B, 0xC0BA6CAD,
  0xEDB88320, 0x9ABFB3B6, 0x03B6E20C, 0x74B1D29A,
  0xEAD54739, 0x9DD277AF, 0x04DB2615, 0x73DC1683,
  0xE3630B12, 0x94643B84, 0x0D6D6A3E, 0x7A6A5AA8,
  0xE40ECF0B, 0x9309FF9D, 0x0A00AE27, 0x7D079EB1,
  0xF00F9344, 0x8708A3D2, 0x1E01F268, 0x6906C2FE,
  0xF762575D, 0x806567CB, 0x196C3671, 0x6E6B06E7,
  0xFED41B76, 0x89D32BE0, 0x10DA7A5A, 0x67DD4ACC,
  0xF9B9DF6F, 0x8EBEEFF9, 0x17B7BE43, 0x60B08ED5,
  0xD6D6A3E8, 0xA1D1937E, 0x38D8C2C4, 0x4FDFF252,
  0xD1BB67F1, 0xA6BC5767, 0x3FB506DD, 0x48B2364B,
  0xD80D2BDA, 0xAF0A1B4C, 0x36034AF6, 0x41047A60,
  0xDF60EFC3, 0xA867DF55, 0x316E8EEF, 0x4669BE79,
  0xCB61B38C, 0xBC66831A, 0x256FD2A0, 0x5268E236,
  0xCC0C7795, 0xBB0B4703, 0x220216B9, 0x5505262F,
  0xC5BA3BBE, 0xB2BD0B28, 0x2BB45A92, 0x5CB36A04,
  0xC2D7FFA7, 0xB5D0CF31, 0x2CD99E8B, 0x5BDEAE1D,
  0x9B64C2B0, 0xEC63F226, 0x756AA39C, 0x026D930A,
  0x9C0906A9, 0xEB0E363F, 0x72076785, 0x05005713,
  0x95BF4A82, 0xE2B87A14, 0x7BB12BAE, 0x0CB61B38,
  0x92D28E9B, 0xE5D5BE0D, 0x7CDCEFB7, 0x0BDBDF21,
  0x86D3D2D4, 0xF1D4E242, 0x68DDB3F8, 0x1FDA836E,
  0x81BE16CD, 0xF6B9265B, 0x6FB077E1, 0x18B74777,
  0x88085AE6, 0xFF0F6A70, 0x66063BCA, 0x11010B5C,
  0x8F659EFF, 0xF862AE69, 0x616BFFD3, 0x166CCF45,
  0xA00AE278, 0xD70DD2EE, 0x4E048354, 0x3903B3C2,
  0xA7672661, 0xD06016F7, 0x4969474D, 0x3E6E77DB,
  0xAED16A4A, 0xD9D65ADC, 0x40DF0B66, 0x37D83BF0,
  0xA9BCAE53, 0xDEBB9EC5, 0x47B2CF7F, 0x30B5FFE9,
  0xBDBDF21C, 0xCABAC28A, 0x53B39330, 0x24B4A3A6,
  0xBAD03605, 0xCDD70693, 0x54DE5729, 0x23D967BF,
  0xB3667A2E, 0xC4614AB8, 0x5D681B02, 0x2A6F2B94,
  0xB40BBE37, 0xC30C8EA1, 0x5A05DF1B, 0x2D02EF8D,
];

// §6.13.14: Safety limits (NORMATIVE)
const MAX_LAYERS = 16;
const MAX_ZONES = 8;
const MAX_LAYER_NOTE_MAPPINGS = 16;
const MAX_OVERRIDES = 100;
const MAX_LED_OVERRIDES = 100;
const MAX_CHORD_NOTES = 8;

/**
 * Calculate CRC32 checksum per §6.13.12
 * Uses lookup table identical to firmware for guaranteed compatibility.
 * Polynomial: 0xEDB88320 (IEEE 802.3)
 * Initial: 0xFFFFFFFF
 * Final XOR: 0xFFFFFFFF
 */
function calculateCRC32(data: Uint8Array): number {
  let crc = 0xFFFFFFFF;

  for (let i = 0; i < data.length; i++) {
    const byte = data[i];
    if (byte === undefined) continue;
    // Lookup table approach: identical to firmware crc32.c
    const index = (crc ^ byte) & 0xFF;
    const tableValue = CRC32_TABLE[index];
    if (tableValue === undefined) continue;
    crc = ((crc >>> 8) ^ tableValue) >>> 0; // Ensure unsigned
  }

  return (crc ^ 0xFFFFFFFF) >>> 0; // Final XOR and ensure unsigned
}

/**
 * Map perceptual profile string to byte value
 * §6.13.6: 0=default, 1=soft, 2=vivid
 */
function profileToByte(profile: string | undefined): number {
  switch (profile) {
    case 'soft': return 1;
    case 'vivid': return 2;
    default: return 0;
  }
}

/**
 * Map byte value to perceptual profile string
 */
function byteToProfile(value: number): 'default' | 'soft' | 'vivid' {
  switch (value) {
    case 1: return 'soft';
    case 2: return 'vivid';
    default: return 'default';
  }
}

/**
 * Map byte value to LED layer mode string
 * §5.13: 0=off, 1=dim, 2=full, 3=pulse
 */
function byteToLedLayerMode(value: number): LEDLayerMode {
  switch (value) {
    case 0: return 'off';
    case 1: return 'dim';
    case 2: return 'full';
    case 3: return 'pulse';
    default: return 'full';
  }
}

/**
 * Convert brightness 0.0-1.0 to byte 0-255
 */
function brightnessToByte(brightness: number): number {
  return Math.round(Math.max(0, Math.min(1, brightness)) * 255);
}

/**
 * Convert byte 0-255 to brightness 0.0-1.0
 */
function byteToBrightness(value: number): number {
  return value / 255;
}

/**
 * Map action type to byte value
 * §6.13.10: Action type encoding
 */
function actionTypeToByte(type: KeyAction['type']): number {
  switch (type) {
    case 'note': return 0;
    case 'cc': return 1;
    case 'program_change': return 2;
    case 'bank_program': return 3;
    case 'layer_toggle': return 4;
    case 'layer_momentary': return 5;
    case 'chord': return 6;
    default: return 0;
  }
}

/**
 * Map byte value to action type
 */
function byteToActionType(value: number): KeyAction['type'] {
  switch (value) {
    case 0: return 'note';
    case 1: return 'cc';
    case 2: return 'program_change';
    case 3: return 'bank_program';
    case 4: return 'layer_toggle';
    case 5: return 'layer_momentary';
    case 6: return 'chord';
    default: return 'note';
  }
}

/**
 * Scale type encoding (v2.1+)
 * Maps directly to firmware's ScaleType_t enum.
 */
function scaleTypeToByte(type: ScaleType | undefined): number {
  switch (type) {
    case 'major':            return 0;
    case 'natural_minor':    return 1;
    case 'harmonic_minor':   return 2;
    case 'melodic_minor':    return 3;
    case 'pentatonic_major': return 4;
    case 'pentatonic_minor': return 5;
    case 'blues':            return 6;
    case 'dorian':           return 7;
    case 'phrygian':         return 8;
    case 'lydian':           return 9;
    case 'mixolydian':       return 10;
    case 'whole_tone':       return 11;
    case 'chromatic':        return 12;
    default:                 return 0; // major
  }
}

function byteToScaleType(value: number): ScaleType {
  switch (value) {
    case 0:  return 'major';
    case 1:  return 'natural_minor';
    case 2:  return 'harmonic_minor';
    case 3:  return 'melodic_minor';
    case 4:  return 'pentatonic_major';
    case 5:  return 'pentatonic_minor';
    case 6:  return 'blues';
    case 7:  return 'dorian';
    case 8:  return 'phrygian';
    case 9:  return 'lydian';
    case 10: return 'mixolydian';
    case 11: return 'whole_tone';
    case 12: return 'chromatic';
    default: return 'major';
  }
}

/**
 * Map slider MIDI type string to byte value (v1.4)
 */
function sliderMidiTypeToByte(type: SliderMidiType): number {
  switch (type) {
    case 'disabled': return 0;
    case 'cc': return 1;
    case 'pitch_bend': return 2;
    case 'aftertouch': return 3;
    default: return 1; // default to CC
  }
}

/**
 * Map byte value to slider MIDI type string (v1.4)
 */
function byteToSliderMidiType(value: number): SliderMidiType {
  switch (value) {
    case 0: return 'disabled';
    case 1: return 'cc';
    case 2: return 'pitch_bend';
    case 3: return 'aftertouch';
    default: return 'cc';
  }
}

/**
 * Map pedal detect mode string to byte value (v1.4)
 */
function pedalDetectModeToByte(mode: PedalDetectMode): number {
  switch (mode) {
    case 'auto': return 0;
    case 'sustain': return 1;
    case 'expression': return 2;
    default: return 0;
  }
}

/**
 * Map byte value to pedal detect mode string (v1.4)
 */
function byteToPedalDetectMode(value: number): PedalDetectMode {
  switch (value) {
    case 0: return 'auto';
    case 1: return 'sustain';
    case 2: return 'expression';
    default: return 'auto';
  }
}

/**
 * Safe byte read from Uint8Array
 */
function readByte(bytes: Uint8Array, offset: number): number {
  const value = bytes[offset];
  return value ?? 0;
}

/**
 * Export configuration to BRI binary format
 * Spec §6.13: Bridge Runtime Image (NORMATIVE)
 *
 * @param store - Current configuration store state
 * @returns ArrayBuffer with binary configuration
 */
export function exportBinaryConfiguration(store: Partial<ConfigStore>): ArrayBuffer {
  // Get configuration values with defaults
  const columns = store.columns ?? 1;
  const rows = store.rows ?? 1;
  const keysPerModule = store.keysPerModule ?? 16;
  const totalKeys = columns * rows * keysPerModule;

  const noteMapping = store.noteMapping ?? {
    origin: { q: 0, r: 0 },
    base_note: 60,
    intervals: { q: 1, r: 12 },
  };

  const ledSettings = store.ledSettings ?? {
    enabled: true,
    brightness: 0.5,
    perceptual_profile: 'default' as const,
    update_rate_hz: 120,
    fade_default_ms: 50,
  };

  const ledDefault = store.ledDefaultBehavior ?? {
    idle: { color: [150, 150, 150] as [number, number, number], brightness: 0.5 },
    pressed: { color: [255, 255, 255] as [number, number, number], brightness: 1.0, fade_ms: 0 },
  };

  const scaleColoring = store.scaleColoring ?? {
    enabled: false,
    root_note: 60,
    scale_type: 'major' as ScaleType,
    colors: {
      root: [255, 0, 0] as [number, number, number],
      in_scale: [0, 100, 255] as [number, number, number],
      non_scale: [80, 80, 80] as [number, number, number],
    },
  };

  const overrides = store.overrides ?? [];
  const ledOverrides = store.ledOverrides ?? [];
  const layers = store.layers ?? [{ id: 0, name: 'default', default: true }];
  const ledLayers = store.ledLayers ?? [];
  const zones = store.zones ?? [];

  const analogConfig = store.analogConfig ?? null;
  const hasAnalogConfig = analogConfig !== null;

  // v2.1: Always emit v2.1 format. The v2 layout strictly supersedes v1.x —
  // it preserves scale_type, all three scale colors (root/in_scale/non_scale),
  // config name, and per-zone note mappings. The TUNING_PRESENT flag still
  // gates the optional Tuning Table section so non-microtonal configs don't
  // pay the size cost. The firmware accepts v1.x, v2.0, and v2.1 — keeping
  // backward compat for any legacy files in the field.
  const tuningEnabled = store.tuningEnabled ?? false;
  const tuningTable = store.tuningTable ?? null;
  const isV2 = true;
  // Track whether tuning section will be written (gates TUNING_PRESENT flag).
  const writeTuningSection = tuningEnabled && tuningTable !== null;

  const vMajor = isV2 ? VERSION_MAJOR_V2 : VERSION_MAJOR;
  const vMinor = isV2 ? VERSION_MINOR_V2 : VERSION_MINOR;

  // Dynamic section sizes based on version
  const noteMappingSize = isV2 ? NOTE_MAPPING_SIZE_V2 : NOTE_MAPPING_SIZE;
  const zoneSize = isV2 ? ZONE_SIZE_V2 : ZONE_SIZE;
  const layerNoteMappingSize = isV2 ? LAYER_NOTE_MAPPING_SIZE_V2 : LAYER_NOTE_MAPPING_SIZE;
  const zoneNoteMappingSize = isV2 ? ZONE_NOTE_MAPPING_SIZE_V2 : ZONE_NOTE_MAPPING_SIZE;
  const perDegreeActive = isV2 && scaleColoring.color_mode === 'per_degree'
    && scaleColoring.degree_colors && scaleColoring.degree_colors.length > 0;
  // When per-degree mode is on without an explicit tuning table, use 12 (12-EDO).
  // The firmware does the same on the parse side.
  const perDegreeCount = perDegreeActive
    ? (tuningTable?.notes_per_period ?? 12)
    : 0;
  const scaleConfigSize = isV2
    ? SCALE_CONFIG_BASE_V2 + (perDegreeCount * 3)
    : SCALE_CONFIG_SIZE;

  let tuningSize = 0;
  if (writeTuningSection && tuningTable) {
    const N = tuningTable.notes_per_period;
    tuningSize = 4 + N * 4 + Math.ceil(N / 8);  // header(4) + pitches(N*4) + mask(ceil(N/8))
  }

  // Build layer note mappings from layers with note_mapping
  const layerNoteMappings: Array<{
    layer_id: number;
    mapping: {
      origin: { q: number; r: number };
      base_note: number;
      intervals: { q: number; r: number };
      cents_mode?: boolean;
      interval_q_cents?: number;
      interval_r_cents?: number;
    };
  }> = [];
  for (const layer of layers) {
    if (layer.note_mapping) {
      layerNoteMappings.push({
        layer_id: layer.id,
        mapping: layer.note_mapping,
      });
    }
  }

  // Build zone note mappings from zones with note_mapping
  const zoneNoteMappings: Array<{
    zone_index: number;
    mapping: {
      origin: { q: number; r: number };
      base_note: number;
      intervals: { q: number; r: number };
      cents_mode?: boolean;
      interval_q_cents?: number;
      interval_r_cents?: number;
    };
  }> = [];
  for (let i = 0; i < zones.length; i++) {
    const zone = zones[i];
    if (zone?.note_mapping) {
      zoneNoteMappings.push({
        zone_index: i,
        mapping: zone.note_mapping,
      });
    }
  }

  const layerCount = Math.min(layers.length, MAX_LAYERS);
  const zoneCount = Math.min(zones.length, MAX_ZONES);
  const layerNoteMappingCount = Math.min(layerNoteMappings.length, MAX_LAYER_NOTE_MAPPINGS);
  const zoneNoteMappingCount = Math.min(zoneNoteMappings.length, MAX_ZONES);
  const overrideCount = Math.min(overrides.length, MAX_OVERRIDES);
  const ledOverrideCount = Math.min(ledOverrides.length, MAX_LED_OVERRIDES);

  // v2.1: Optional config name section (32 bytes)
  // Always include in v2 exports if there's a name to preserve.
  const configName = (store.configName ?? '').trim();
  const hasConfigName = isV2 && configName.length > 0;

  // Calculate total size
  const totalSize =
    HEADER_SIZE +
    GRID_CONFIG_SIZE +
    noteMappingSize +
    MIDI_CONFIG_SIZE +
    (hasAnalogConfig ? ANALOG_INPUTS_SIZE : 0) +
    tuningSize +
    LED_SETTINGS_SIZE +
    LED_DEFAULT_SIZE +
    scaleConfigSize +
    layerCount * LAYER_SIZE +
    zoneCount * zoneSize +
    layerNoteMappingCount * layerNoteMappingSize +
    zoneNoteMappingCount * zoneNoteMappingSize +
    overrideCount * OVERRIDE_SIZE +
    ledOverrideCount * LED_OVERRIDE_SIZE +
    (hasConfigName ? CONFIG_NAME_SIZE : 0) +
    CRC32_SIZE;

  const buffer = new ArrayBuffer(totalSize);
  const view = new DataView(buffer);
  const bytes = new Uint8Array(buffer);
  let offset = 0;

  // === Header (20 bytes) ===
  bytes[offset++] = MAGIC_BYTES[0];
  bytes[offset++] = MAGIC_BYTES[1];
  bytes[offset++] = MAGIC_BYTES[2];
  bytes[offset++] = MAGIC_BYTES[3];
  bytes[offset++] = vMajor;
  bytes[offset++] = vMinor;
  bytes[offset++] = 0; // patch
  let headerFlags = 0;
  if (ledSettings.enabled) headerFlags |= BRI_FLAG_LED_ENABLED;
  if (hasAnalogConfig) headerFlags |= BRI_FLAG_ANALOG_PRESENT;
  if (writeTuningSection && tuningTable) headerFlags |= BRI_FLAG_TUNING_PRESENT;
  if (hasConfigName) headerFlags |= BRI_FLAG_HAS_NAME;
  bytes[offset++] = headerFlags;
  bytes[offset++] = columns;
  bytes[offset++] = rows;
  bytes[offset++] = layerCount;
  bytes[offset++] = zoneCount;
  view.setUint16(offset, overrideCount, true);
  offset += 2;
  view.setUint16(offset, ledOverrideCount, true);
  offset += 2;
  view.setUint16(offset, layerNoteMappingCount, true);
  offset += 2;
  bytes[offset++] = keysPerModule;
  bytes[offset++] = zoneNoteMappingCount; // byte 19: zone note mapping count

  // === Grid Config (4 bytes) ===
  view.setUint16(offset, totalKeys, true);
  offset += 2;
  // Module row shift: stored as (value + 128) so old files (0x00) decode as invalid → fallback to 0
  const moduleRowShift = store.moduleRowShift ?? 0;
  bytes[offset++] = (moduleRowShift + 128) & 0xFF;
  bytes[offset++] = 0x00; // reserved

  // === Note Mapping (8 or 16 bytes) ===
  if (isV2) {
    view.setInt8(offset++, noteMapping.origin.q);
    view.setInt8(offset++, noteMapping.origin.r);
    bytes[offset++] = noteMapping.base_note;
    bytes[offset++] = 0x01; // flags: cents_mode = 1
    // Derive cents from integer semitone intervals when the user hasn't
    // explicitly set cent fields (typical non-microtonal config).
    const nmQCents = noteMapping.interval_q_cents ?? (noteMapping.intervals.q * 1000);
    const nmRCents = noteMapping.interval_r_cents ?? (noteMapping.intervals.r * 1000);
    view.setInt16(offset, nmQCents, true); offset += 2;
    view.setInt16(offset, nmRCents, true); offset += 2;
    bytes[offset++] = noteMapping.pitch_bend_range ?? 48;
    for (let i = 0; i < 7; i++) bytes[offset++] = 0; // reserved
  } else {
    view.setInt8(offset++, noteMapping.origin.q);
    view.setInt8(offset++, noteMapping.origin.r);
    bytes[offset++] = noteMapping.base_note;
    bytes[offset++] = 0x00; // Reserved
    view.setInt8(offset++, noteMapping.intervals.q);
    view.setInt8(offset++, noteMapping.intervals.r);
    view.setUint16(offset, 0x0000, true);
    offset += 2;
  }

  // === MIDI Config (4 bytes) ===
  bytes[offset++] = 0; // default_channel
  bytes[offset++] = 0; // velocity_curve
  bytes[offset++] = 1; // velocity_min
  bytes[offset++] = 127; // velocity_max

  // === Analog Inputs Config (40 bytes, optional) ===
  if (hasAnalogConfig && analogConfig) {
    bytes[offset++] = 1; // version
    bytes[offset++] = MAX_SLIDERS;
    bytes[offset++] = 0; // flags
    for (let i = 0; i < 5; i++) bytes[offset++] = 0;

    for (let i = 0; i < MAX_SLIDERS; i++) {
      const slider = analogConfig.sliders[i];
      bytes[offset++] = sliderMidiTypeToByte(slider?.midi_type ?? 'cc');
      bytes[offset++] = slider?.channel ?? 0;
      bytes[offset++] = slider?.cc ?? (i + 1);
      bytes[offset++] = slider?.min ?? 0;
      bytes[offset++] = slider?.max ?? 127;
      let sliderFlags = 0;
      if (slider?.invert) sliderFlags |= 0x01;
      if (slider?.spring_center) sliderFlags |= 0x02;
      bytes[offset++] = sliderFlags;
      bytes[offset++] = 0;
      bytes[offset++] = 0;
    }

    const pedal = analogConfig.pedal;
    bytes[offset++] = pedalDetectModeToByte(pedal?.detect_mode ?? 'auto');
    bytes[offset++] = pedal?.sustain?.cc ?? 64;
    bytes[offset++] = pedal?.sustain?.channel ?? 0;
    bytes[offset++] = pedal?.expression?.cc ?? 11;
    bytes[offset++] = pedal?.expression?.channel ?? 0;
    bytes[offset++] = pedal?.expression?.min ?? 0;
    bytes[offset++] = pedal?.expression?.max ?? 127;
    let pedalFlags = 0;
    if (pedal?.sustain?.invert) pedalFlags |= 0x01;
    if (pedal?.expression?.invert) pedalFlags |= 0x02;
    bytes[offset++] = pedalFlags;
  }

  // === Tuning Section (v2.0 only, variable size) ===
  if (writeTuningSection && tuningTable) {
    const N = tuningTable.notes_per_period;
    bytes[offset++] = N;
    bytes[offset++] = tuningTable.period_is_octave ? 0x01 : 0x00;
    view.setUint16(offset, tuningTable.period_cents, true); offset += 2;

    for (let i = 0; i < N; i++) {
      view.setInt32(offset, tuningTable.pitches[i] ?? 0, true); offset += 4;
    }

    // Scale mask: ceil(N/8) bytes, LSB-first bit packing
    const maskLen = Math.ceil(N / 8);
    for (let byteIdx = 0; byteIdx < maskLen; byteIdx++) {
      let maskByte = 0;
      for (let bit = 0; bit < 8; bit++) {
        const degreeIdx = byteIdx * 8 + bit;
        if (degreeIdx < N && tuningTable.scale_mask[degreeIdx]) {
          maskByte |= (1 << bit);
        }
      }
      bytes[offset++] = maskByte;
    }
  }

  // === LED Settings (16 bytes) ===
  bytes[offset++] = ledSettings.enabled ? 1 : 0;
  bytes[offset++] = brightnessToByte(ledSettings.brightness);
  bytes[offset++] = profileToByte(ledSettings.perceptual_profile);
  bytes[offset++] = 0x00;
  view.setUint16(offset, ledSettings.update_rate_hz, true);
  offset += 2;
  view.setUint16(offset, ledSettings.fade_default_ms, true);
  offset += 2;
  for (let i = 0; i < 8; i++) bytes[offset++] = 0x00;

  // === LED Default Behavior (16 bytes) ===
  bytes[offset++] = ledDefault.idle.color[0];
  bytes[offset++] = ledDefault.idle.color[1];
  bytes[offset++] = ledDefault.idle.color[2];
  bytes[offset++] = brightnessToByte(ledDefault.idle.brightness);
  bytes[offset++] = ledDefault.pressed.color[0];
  bytes[offset++] = ledDefault.pressed.color[1];
  bytes[offset++] = ledDefault.pressed.color[2];

  // Auto-clamp pressed brightness to stay within power budget
  const powerBudget = store.powerBudget ?? { converterAmps: 3.0, maxSimultaneousPressed: 10 };
  const maxSafePressedBrightness = calculateMaxPressedBrightness({
    columns,
    rows,
    keysPerModule,
    globalBrightness: ledSettings.brightness,
    idleColor: ledDefault.idle.color as [number, number, number],
    idleBrightness: ledDefault.idle.brightness,
    pressedColor: ledDefault.pressed.color as [number, number, number],
    pressedBrightness: ledDefault.pressed.brightness,
    maxSimultaneousPressed: powerBudget.maxSimultaneousPressed,
    converterAmps: powerBudget.converterAmps,
  });
  const clampedPressedBrightness = Math.min(ledDefault.pressed.brightness, maxSafePressedBrightness);
  bytes[offset++] = brightnessToByte(clampedPressedBrightness);
  view.setUint16(offset, ledDefault.pressed.fade_ms ?? 0, true);
  offset += 2;
  for (let i = 0; i < 6; i++) bytes[offset++] = 0x00;

  // === Scale Config (40 bytes v1.x, 13 bytes v2.0) ===
  if (isV2) {
    bytes[offset++] = scaleColoring.enabled ? 1 : 0;
    // For 12-EDO non-microtonal configs the user only sets root_note (a MIDI
    // note 0-127). Derive the degree index from root_note % 12 when explicit
    // root_degree isn't present, so the firmware stores a sensible value.
    const derivedRootDegree = scaleColoring.root_degree
      ?? (scaleColoring.root_note !== undefined ? scaleColoring.root_note % 12 : 0);
    bytes[offset++] = derivedRootDegree;
    bytes[offset++] = 128; // brightness
    bytes[offset++] = perDegreeActive ? 1 : 0;   // color_mode
    bytes[offset++] = scaleColoring.colors.root[0];
    bytes[offset++] = scaleColoring.colors.root[1];
    bytes[offset++] = scaleColoring.colors.root[2];
    bytes[offset++] = scaleColoring.colors.in_scale[0];
    bytes[offset++] = scaleColoring.colors.in_scale[1];
    bytes[offset++] = scaleColoring.colors.in_scale[2];
    bytes[offset++] = scaleColoring.colors.non_scale[0];
    bytes[offset++] = scaleColoring.colors.non_scale[1];
    bytes[offset++] = scaleColoring.colors.non_scale[2];
    // v2.1: scale_type byte for round-trip preservation
    bytes[offset++] = scaleTypeToByte(scaleColoring.scale_type);
    // Per-degree colors (N*3 bytes when color_mode=1).
    // Without an explicit tuning table, default to N=12 (12-EDO) — matches
    // the firmware parser's default and keeps the size calc consistent.
    if (perDegreeActive && scaleColoring.degree_colors) {
      for (let i = 0; i < perDegreeCount; i++) {
        const c = scaleColoring.degree_colors[i] ?? [128, 128, 128];
        bytes[offset++] = c[0];
        bytes[offset++] = c[1];
        bytes[offset++] = c[2];
      }
    }
  } else {
    bytes[offset++] = scaleColoring.enabled ? 1 : 0;
    bytes[offset++] = scaleColoring.root_note;
    bytes[offset++] = 128;
    bytes[offset++] = 0x00;

    const v1PerDegree = scaleColoring.color_mode === 'per_degree'
      && scaleColoring.degree_colors && scaleColoring.degree_colors.length === 12;
    const rootSemitone = scaleColoring.root_note % 12;
    const scaleMask = SCALE_MASKS[scaleColoring.scale_type] ?? SCALE_MASKS.major;
    for (let i = 0; i < 12; i++) {
      const interval = (i - rootSemitone + 12) % 12;
      let color: [number, number, number];
      if (v1PerDegree && scaleColoring.degree_colors) {
        // Per-degree mode: use custom color for each chromatic interval
        color = scaleColoring.degree_colors[interval] ?? [128, 128, 128];
      } else if (interval === 0) {
        color = scaleColoring.colors.root;
      } else if (scaleMask[interval] === 1) {
        color = scaleColoring.colors.in_scale;
      } else {
        color = scaleColoring.colors.non_scale;
      }
      bytes[offset++] = color[0];
      bytes[offset++] = color[1];
      bytes[offset++] = color[2];
    }
  }

  // === Layer entries (8 bytes each, same for v1.x and v2.0) ===
  for (let i = 0; i < layerCount; i++) {
    const layer = layers[i];
    if (!layer) continue;

    const ledConfig = ledLayers.find((l) => l.layer === layer.id);
    bytes[offset++] = layer.id;
    bytes[offset++] = layer.default ? 0x01 : 0x00;

    if (ledConfig) {
      bytes[offset++] = ledConfig.base_color[0];
      bytes[offset++] = ledConfig.base_color[1];
      bytes[offset++] = ledConfig.base_color[2];
      bytes[offset++] = brightnessToByte(ledConfig.brightness);
      bytes[offset++] = ledConfig.mode === 'off' ? 0
                      : ledConfig.mode === 'dim' ? 1
                      : ledConfig.mode === 'full' ? 2
                      : ledConfig.mode === 'pulse' ? 3 : 2;
    } else {
      bytes[offset++] = 0;
      bytes[offset++] = 0;
      bytes[offset++] = 0;
      bytes[offset++] = 0;
      bytes[offset++] = 0; // mode OFF - no layer color when unconfigured
    }
    bytes[offset++] = 0x00;
  }

  // === Zone entries (8 bytes v1.x, 10 bytes v2.0) ===
  for (let i = 0; i < zoneCount; i++) {
    const zone = zones[i];
    if (!zone) continue;

    view.setInt8(offset++, zone.bounds.q_min);
    view.setInt8(offset++, zone.bounds.q_max);
    view.setInt8(offset++, zone.bounds.r_min ?? -128);
    view.setInt8(offset++, zone.bounds.r_max ?? -128);
    bytes[offset++] = zone.channel;
    view.setInt8(offset++, Math.max(-24, Math.min(24, zone.transpose)));
    if (isV2) {
      view.setInt16(offset, zone.transpose_cents ?? 0, true); offset += 2;
      view.setUint16(offset, 0, true); offset += 2; // reserved
    } else {
      bytes[offset++] = 0x00;
      bytes[offset++] = 0x00;
    }
  }

  // === Layer Note Mapping entries (8 bytes v1.x, 12 bytes v2.0) ===
  for (let i = 0; i < layerNoteMappingCount; i++) {
    const entry = layerNoteMappings[i];
    if (!entry) continue;
    const mapping = entry.mapping;

    if (isV2) {
      bytes[offset++] = entry.layer_id;
      bytes[offset++] = mapping.cents_mode ? 0x01 : 0x00;
      view.setInt8(offset++, mapping.origin.q);
      view.setInt8(offset++, mapping.origin.r);
      bytes[offset++] = mapping.base_note;
      bytes[offset++] = 0x00; // reserved
      view.setInt16(offset, mapping.interval_q_cents ?? (mapping.intervals.q * 1000), true); offset += 2;
      view.setInt16(offset, mapping.interval_r_cents ?? (mapping.intervals.r * 1000), true); offset += 2;
      view.setUint16(offset, 0, true); offset += 2; // reserved
    } else {
      bytes[offset++] = entry.layer_id;
      bytes[offset++] = 0x00;
      view.setInt8(offset++, mapping.origin.q);
      view.setInt8(offset++, mapping.origin.r);
      bytes[offset++] = mapping.base_note;
      view.setInt8(offset++, mapping.intervals.q);
      view.setInt8(offset++, mapping.intervals.r);
      bytes[offset++] = 0x00;
    }
  }

  // === Zone Note Mapping entries (8 bytes v1.x, 12 bytes v2.0) ===
  for (let i = 0; i < zoneNoteMappingCount; i++) {
    const entry = zoneNoteMappings[i];
    if (!entry) continue;
    const mapping = entry.mapping;

    if (isV2) {
      bytes[offset++] = entry.zone_index;
      bytes[offset++] = mapping.cents_mode ? 0x01 : 0x00;
      view.setInt8(offset++, mapping.origin.q);
      view.setInt8(offset++, mapping.origin.r);
      bytes[offset++] = mapping.base_note;
      bytes[offset++] = 0x00; // reserved
      view.setInt16(offset, mapping.interval_q_cents ?? (mapping.intervals.q * 1000), true); offset += 2;
      view.setInt16(offset, mapping.interval_r_cents ?? (mapping.intervals.r * 1000), true); offset += 2;
      view.setUint16(offset, 0, true); offset += 2; // reserved
    } else {
      bytes[offset++] = entry.zone_index;
      bytes[offset++] = 0x00;
      view.setInt8(offset++, mapping.origin.q);
      view.setInt8(offset++, mapping.origin.r);
      bytes[offset++] = mapping.base_note;
      view.setInt8(offset++, mapping.intervals.q);
      view.setInt8(offset++, mapping.intervals.r);
      bytes[offset++] = 0x00;
    }
  }

  // === Override entries (16 bytes each, same for v1.x and v2.0) ===
  for (let i = 0; i < overrideCount; i++) {
    const override = overrides[i];
    if (!override) continue;

    view.setInt8(offset++, override.pos.q);
    view.setInt8(offset++, override.pos.r);
    bytes[offset++] = override.layer;
    bytes[offset++] = actionTypeToByte(override.action.type);

    const actionDataStart = offset;
    switch (override.action.type) {
      case 'note':
        bytes[offset++] = override.action.note;
        bytes[offset++] = override.action.channel;
        break;
      case 'cc':
        bytes[offset++] = override.action.cc;
        bytes[offset++] = override.action.value;
        bytes[offset++] = override.action.channel;
        break;
      case 'program_change':
        bytes[offset++] = override.action.program;
        bytes[offset++] = override.action.channel;
        break;
      case 'bank_program':
        bytes[offset++] = override.action.program;
        bytes[offset++] = override.action.channel;
        bytes[offset++] = override.action.bank_msb ?? 0;
        bytes[offset++] = override.action.bank_lsb ?? 0;
        break;
      case 'chord': {
        const noteCount = Math.min(override.action.notes.length, MAX_CHORD_NOTES);
        bytes[offset++] = noteCount;
        bytes[offset++] = override.action.channel;
        for (let n = 0; n < MAX_CHORD_NOTES; n++) {
          bytes[offset++] = override.action.notes[n] ?? 0;
        }
        break;
      }
      case 'layer_toggle':
      case 'layer_momentary':
        bytes[offset++] = override.action.target_layer;
        break;
      default:
        break;
    }
    while (offset < actionDataStart + 12) {
      bytes[offset++] = 0;
    }
  }

  // === LED Override entries (12 bytes each) ===
  for (let i = 0; i < ledOverrideCount; i++) {
    const override = ledOverrides[i];
    if (!override) continue;

    view.setInt8(offset++, override.pos.q);
    view.setInt8(offset++, override.pos.r);
    bytes[offset++] = override.layer;
    bytes[offset++] = override.pressed ? 0x01 : 0x00;

    bytes[offset++] = override.idle.color[0];
    bytes[offset++] = override.idle.color[1];
    bytes[offset++] = override.idle.color[2];
    bytes[offset++] = brightnessToByte(override.idle.brightness);

    if (override.pressed) {
      bytes[offset++] = override.pressed.color[0];
      bytes[offset++] = override.pressed.color[1];
      bytes[offset++] = override.pressed.color[2];
      bytes[offset++] = brightnessToByte(override.pressed.brightness);
    } else {
      bytes[offset++] = override.idle.color[0];
      bytes[offset++] = override.idle.color[1];
      bytes[offset++] = override.idle.color[2];
      bytes[offset++] = brightnessToByte(override.idle.brightness);
    }
  }

  // === Optional: Config name (32 bytes, v2.1+ when HAS_NAME flag set) ===
  if (hasConfigName) {
    // UTF-8 encode, truncate to 31 bytes, null-terminate, zero-pad to 32 bytes
    const enc = new TextEncoder();
    const nameBytes = enc.encode(configName);
    const limit = Math.min(nameBytes.length, CONFIG_NAME_SIZE - 1);
    for (let i = 0; i < limit; i++) bytes[offset + i] = nameBytes[i] ?? 0;
    for (let i = limit; i < CONFIG_NAME_SIZE; i++) bytes[offset + i] = 0;
    offset += CONFIG_NAME_SIZE;
  }

  // === CRC32 (4 bytes) ===
  const dataForCrc = new Uint8Array(buffer, 0, offset);
  const crc = calculateCRC32(dataForCrc);
  view.setUint32(offset, crc, true);

  return buffer;
}

/**
 * Download binary configuration as .bri file
 *
 * @param buffer - Binary configuration buffer
 * @param filename - Output filename (default: "keyboard-config.bri")
 */
export function downloadBinaryConfiguration(
  buffer: ArrayBuffer,
  filename: string = 'keyboard-config.bri'
): void {
  const blob = new Blob([buffer], { type: 'application/octet-stream' });
  const url = URL.createObjectURL(blob);

  // Create temporary download link
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();

  // Cleanup
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Validation result for binary import
 */
export interface BinaryValidationResult {
  valid: boolean;
  errors?: string[];
  config?: Partial<ConfigStore>;
}

/**
 * Parse and validate binary BRI configuration
 * Spec §6.13: Validates all required fields
 *
 * @param buffer - ArrayBuffer from uploaded file
 * @returns Validation result with parsed config or errors
 */
export function parseBinaryConfiguration(buffer: ArrayBuffer): BinaryValidationResult {
  const errors: string[] = [];
  const bytes = new Uint8Array(buffer);
  const view = new DataView(buffer);

  // Minimum size check
  const minSize = HEADER_SIZE + GRID_CONFIG_SIZE + NOTE_MAPPING_SIZE +
    MIDI_CONFIG_SIZE + LED_SETTINGS_SIZE + LED_DEFAULT_SIZE +
    SCALE_CONFIG_SIZE + LAYER_SIZE + CRC32_SIZE;

  if (buffer.byteLength < minSize) {
    return {
      valid: false,
      errors: [`File too small: expected at least ${minSize} bytes, got ${buffer.byteLength}`],
    };
  }

  let offset = 0;

  // Validate magic bytes
  if (readByte(bytes, 0) !== MAGIC_BYTES[0] || readByte(bytes, 1) !== MAGIC_BYTES[1] ||
      readByte(bytes, 2) !== MAGIC_BYTES[2] || readByte(bytes, 3) !== MAGIC_BYTES[3]) {
    return {
      valid: false,
      errors: ['Invalid magic bytes: expected "STAK"'],
    };
  }
  offset += 4;

  // Validate version (accept v1.3, v1.4, v2.0, v2.1)
  const versionMajor = readByte(bytes, offset++);
  const versionMinor = readByte(bytes, offset++);
  const versionPatch = readByte(bytes, offset++);

  const isV2 = (versionMajor === 2 && (versionMinor === 0 || versionMinor === 1));
  const isV21 = (versionMajor === 2 && versionMinor === 1);  // v2.1 has scale_type byte
  const isV1 = (versionMajor === 1 && (versionMinor === 3 || versionMinor === 4));

  if (!isV1 && !isV2) {
    errors.push(`Version mismatch: expected 1.3.x, 1.4.x, 2.0.x, or 2.1.x, got ${versionMajor}.${versionMinor}.${versionPatch}`);
  }

  // Parse header fields
  const flags = readByte(bytes, offset++);
  const ledEnabledFlag = (flags & BRI_FLAG_LED_ENABLED) !== 0;
  const hasAnalogConfig = (flags & BRI_FLAG_ANALOG_PRESENT) !== 0;
  const hasTuning = (flags & BRI_FLAG_TUNING_PRESENT) !== 0;
  const hasName = (flags & BRI_FLAG_HAS_NAME) !== 0;

  const moduleCols = readByte(bytes, offset++);
  const moduleRows = readByte(bytes, offset++);
  const layerCount = readByte(bytes, offset++);
  const zoneCount = readByte(bytes, offset++);

  const overrideCount = view.getUint16(offset, true);
  offset += 2;
  const ledOverrideCount = view.getUint16(offset, true);
  offset += 2;
  const layerNoteMappingCount = view.getUint16(offset, true);
  offset += 2;
  const kpmByte = readByte(bytes, offset++);
  const keysPerModule: 12 | 16 = (kpmByte === 12) ? 12 : 16;
  const zoneNoteMappingCount = readByte(bytes, offset++); // byte 19

  // Validate header fields
  if (moduleCols < 1 || moduleCols > 8) {
    errors.push(`Invalid module_cols: must be 1-8, got ${moduleCols}`);
  }
  if (moduleRows < 1 || moduleRows > 4) {
    errors.push(`Invalid module_rows: must be 1-4, got ${moduleRows}`);
  }
  if (moduleCols * moduleRows > 32) {
    errors.push(`Invalid grid: cols × rows must be ≤ 32, got ${moduleCols * moduleRows}`);
  }
  if (layerCount < 1 || layerCount > MAX_LAYERS) {
    errors.push(`Invalid layer_count: must be 1-${MAX_LAYERS}, got ${layerCount}`);
  }
  if (zoneCount > MAX_ZONES) {
    errors.push(`Too many zones: max ${MAX_ZONES}, got ${zoneCount}`);
  }
  if (layerNoteMappingCount > MAX_LAYER_NOTE_MAPPINGS) {
    errors.push(`Too many layer note mappings: max ${MAX_LAYER_NOTE_MAPPINGS}, got ${layerNoteMappingCount}`);
  }
  if (zoneNoteMappingCount > MAX_ZONES) {
    errors.push(`Too many zone note mappings: max ${MAX_ZONES}, got ${zoneNoteMappingCount}`);
  }
  if (overrideCount > MAX_OVERRIDES) {
    errors.push(`Too many overrides: max ${MAX_OVERRIDES}, got ${overrideCount}`);
  }
  if (ledOverrideCount > MAX_LED_OVERRIDES) {
    errors.push(`Too many LED overrides: max ${MAX_LED_OVERRIDES}, got ${ledOverrideCount}`);
  }

  // Size validation: v2.0 uses >= (minimum) due to variable-size tuning/scale sections
  const noteMappingSizeUsed = isV2 ? NOTE_MAPPING_SIZE_V2 : NOTE_MAPPING_SIZE;
  const zoneSizeUsed = isV2 ? ZONE_SIZE_V2 : ZONE_SIZE;
  const layerNMSizeUsed = isV2 ? LAYER_NOTE_MAPPING_SIZE_V2 : LAYER_NOTE_MAPPING_SIZE;
  const zoneNMSizeUsed = isV2 ? ZONE_NOTE_MAPPING_SIZE_V2 : ZONE_NOTE_MAPPING_SIZE;
  const scaleConfigSizeUsed = isV2 ? SCALE_CONFIG_BASE_V2 : SCALE_CONFIG_SIZE;

  const expectedMinSize = HEADER_SIZE + GRID_CONFIG_SIZE + noteMappingSizeUsed +
    MIDI_CONFIG_SIZE +
    (hasAnalogConfig ? ANALOG_INPUTS_SIZE : 0) +
    (isV2 && hasTuning ? 4 : 0) + // minimum tuning header
    LED_SETTINGS_SIZE + LED_DEFAULT_SIZE +
    scaleConfigSizeUsed + layerCount * LAYER_SIZE +
    zoneCount * zoneSizeUsed + layerNoteMappingCount * layerNMSizeUsed +
    zoneNoteMappingCount * zoneNMSizeUsed +
    overrideCount * OVERRIDE_SIZE + ledOverrideCount * LED_OVERRIDE_SIZE +
    CRC32_SIZE;

  if (isV2) {
    if (buffer.byteLength < expectedMinSize) {
      errors.push(`Size too small: expected at least ${expectedMinSize} bytes, got ${buffer.byteLength}`);
    }
  } else {
    if (buffer.byteLength !== expectedMinSize) {
      errors.push(`Size mismatch: expected ${expectedMinSize} bytes, got ${buffer.byteLength}`);
    }
  }

  // Validate CRC32 before continuing
  const crcOffset = buffer.byteLength - CRC32_SIZE;
  const dataForCrc = new Uint8Array(buffer, 0, crcOffset);
  const calculatedCrc = calculateCRC32(dataForCrc);
  const storedCrc = view.getUint32(crcOffset, true);

  if (calculatedCrc !== storedCrc) {
    return {
      valid: false,
      errors: [`CRC32 mismatch: expected 0x${calculatedCrc.toString(16).toUpperCase()}, got 0x${storedCrc.toString(16).toUpperCase()}`],
    };
  }

  if (errors.length > 0) {
    return { valid: false, errors };
  }

  // === Parse Grid Config (4 bytes) ===
  const totalKeys = view.getUint16(offset, true);
  offset += 2;
  // Module row shift: stored as (value + 128); 0x00 from old files → use default 0 (no extra shift)
  const moduleRowShiftEncoded = readByte(bytes, offset++);
  const parsedModuleRowShift = moduleRowShiftEncoded === 0 ? 0 : (moduleRowShiftEncoded - 128);
  offset++; // reserved

  const columns = moduleCols;
  const rows = moduleRows;

  if (totalKeys !== columns * rows * keysPerModule) {
    errors.push(`Total keys mismatch: expected ${columns * rows * keysPerModule}, got ${totalKeys}`);
  }

  // === Parse Note Mapping (8 or 16 bytes) ===
  let parsedNoteMapping: {
    origin: { q: number; r: number };
    base_note: number;
    intervals: { q: number; r: number };
    cents_mode?: boolean;
    interval_q_cents?: number;
    interval_r_cents?: number;
    pitch_bend_range?: number;
  };

  if (isV2) {
    const originQ = view.getInt8(offset++);
    const originR = view.getInt8(offset++);
    const baseNote = readByte(bytes, offset++);
    const nmFlags = readByte(bytes, offset++);
    const intervalQCents = view.getInt16(offset, true); offset += 2;
    const intervalRCents = view.getInt16(offset, true); offset += 2;
    const pitchBendRange = readByte(bytes, offset++);
    offset += 7; // reserved

    parsedNoteMapping = {
      origin: { q: originQ, r: originR },
      base_note: baseNote,
      intervals: {
        q: Math.round(intervalQCents / 1000),
        r: Math.round(intervalRCents / 1000),
      },
      cents_mode: (nmFlags & 0x01) !== 0,
      interval_q_cents: intervalQCents,
      interval_r_cents: intervalRCents,
      pitch_bend_range: pitchBendRange || 48,
    };
  } else {
    const originQ = view.getInt8(offset++);
    const originR = view.getInt8(offset++);
    const baseNote = readByte(bytes, offset++);
    offset++; // Reserved
    const intervalQ = view.getInt8(offset++);
    const intervalR = view.getInt8(offset++);
    offset += 2; // Reserved

    if (baseNote > 127) {
      errors.push(`Invalid base_note: must be 0-127, got ${baseNote}`);
    }

    parsedNoteMapping = {
      origin: { q: originQ, r: originR },
      base_note: baseNote,
      intervals: { q: intervalQ, r: intervalR },
    };
  }

  // === MIDI Config (4 bytes) - skip ===
  offset += MIDI_CONFIG_SIZE;

  // === Analog Inputs (40 bytes, optional) ===
  let parsedAnalogConfig: AnalogConfig | null = null;
  if (hasAnalogConfig) {
    offset++; // version
    offset++; // sliderCount
    offset++; // flags
    offset += 5; // reserved

    const sliders: [SliderConfig, SliderConfig, SliderConfig] = [
      { midi_type: 'cc', channel: 0, cc: 1, min: 0, max: 127, invert: false, spring_center: false },
      { midi_type: 'cc', channel: 0, cc: 2, min: 0, max: 127, invert: false, spring_center: false },
      { midi_type: 'cc', channel: 0, cc: 3, min: 0, max: 127, invert: false, spring_center: false },
    ];
    for (let i = 0; i < MAX_SLIDERS; i++) {
      const midiType = byteToSliderMidiType(readByte(bytes, offset++));
      const channel = readByte(bytes, offset++);
      const cc = readByte(bytes, offset++);
      const min = readByte(bytes, offset++);
      const max = readByte(bytes, offset++);
      const sliderFlags = readByte(bytes, offset++);
      offset += 2;
      sliders[i] = {
        midi_type: midiType,
        channel,
        cc,
        min,
        max,
        invert: (sliderFlags & 0x01) !== 0,
        spring_center: (sliderFlags & 0x02) !== 0,
      };
    }

    const detectMode = byteToPedalDetectMode(readByte(bytes, offset++));
    const sustainCc = readByte(bytes, offset++);
    const sustainChannel = readByte(bytes, offset++);
    const expressionCc = readByte(bytes, offset++);
    const expressionChannel = readByte(bytes, offset++);
    const expressionMin = readByte(bytes, offset++);
    const expressionMax = readByte(bytes, offset++);
    const pedalFlags = readByte(bytes, offset++);

    parsedAnalogConfig = {
      sliders,
      pedal: {
        detect_mode: detectMode,
        sustain: { cc: sustainCc, channel: sustainChannel, invert: (pedalFlags & 0x01) !== 0 },
        expression: { cc: expressionCc, channel: expressionChannel, min: expressionMin, max: expressionMax, invert: (pedalFlags & 0x02) !== 0 },
      },
    };
  }

  // === Tuning Section (v2.0, variable size) ===
  let parsedTuningEnabled = false;
  let parsedTuningTable: TuningTable | undefined = undefined;

  if (isV2 && hasTuning) {
    parsedTuningEnabled = true;
    const notesPerPeriod = readByte(bytes, offset++);
    const tuningFlags = readByte(bytes, offset++);
    const periodCents = view.getUint16(offset, true); offset += 2;

    const pitches: number[] = [];
    for (let i = 0; i < notesPerPeriod; i++) {
      pitches.push(view.getInt32(offset, true));
      offset += 4;
    }

    const maskLen = Math.ceil(notesPerPeriod / 8);
    const scaleMask: boolean[] = [];
    for (let byteIdx = 0; byteIdx < maskLen; byteIdx++) {
      const maskByte = readByte(bytes, offset++);
      for (let bit = 0; bit < 8; bit++) {
        const idx = byteIdx * 8 + bit;
        if (idx < notesPerPeriod) {
          scaleMask.push((maskByte & (1 << bit)) !== 0);
        }
      }
    }

    parsedTuningTable = {
      notes_per_period: notesPerPeriod,
      period_is_octave: (tuningFlags & 0x01) !== 0,
      period_cents: periodCents,
      pitches,
      scale_mask: scaleMask,
    };
  }

  // === LED Settings (16 bytes) ===
  const ledEnabledSetting = readByte(bytes, offset++) !== 0;
  const brightness = byteToBrightness(readByte(bytes, offset++));
  const perceptualProfile = byteToProfile(readByte(bytes, offset++));
  offset++;
  const updateRateHz = view.getUint16(offset, true);
  offset += 2;
  const fadeDefaultMs = view.getUint16(offset, true);
  offset += 2;
  offset += 8;

  if (updateRateHz < 30 || updateRateHz > 200) {
    errors.push(`Invalid update_rate_hz: must be 30-200, got ${updateRateHz}`);
  }
  if (fadeDefaultMs > 500) {
    errors.push(`Invalid fade_default_ms: must be 0-500, got ${fadeDefaultMs}`);
  }

  // === LED Default Behavior (16 bytes) ===
  const idleR = readByte(bytes, offset++);
  const idleG = readByte(bytes, offset++);
  const idleB = readByte(bytes, offset++);
  const idleBrightness = byteToBrightness(readByte(bytes, offset++));
  const pressedR = readByte(bytes, offset++);
  const pressedG = readByte(bytes, offset++);
  const pressedB = readByte(bytes, offset++);
  const pressedBrightness = byteToBrightness(readByte(bytes, offset++));
  const pressedFadeMs = view.getUint16(offset, true);
  offset += 2;
  offset += 6;

  // === Scale Config (40 bytes v1.x, 13+ bytes v2.0) ===
  let parsedScaleColoring: {
    enabled: boolean;
    root_note: number;
    root_degree?: number;
    scale_type: ScaleType;
    colors: {
      root: [number, number, number];
      in_scale: [number, number, number];
      non_scale: [number, number, number];
    };
    color_mode?: 'three_color' | 'per_degree';
    degree_colors?: [number, number, number][];
  };

  if (isV2) {
    const scaleEnabled = readByte(bytes, offset++) !== 0;
    const rootDegree = readByte(bytes, offset++);
    offset++; // brightness (reserved/unused)
    const colorMode = readByte(bytes, offset++);

    const rootRGB: [number, number, number] = [readByte(bytes, offset++), readByte(bytes, offset++), readByte(bytes, offset++)];
    const inScaleRGB: [number, number, number] = [readByte(bytes, offset++), readByte(bytes, offset++), readByte(bytes, offset++)];
    const nonScaleRGB: [number, number, number] = [readByte(bytes, offset++), readByte(bytes, offset++), readByte(bytes, offset++)];

    // v2.1: scale_type byte. v2.0 files: default to chromatic (any color is "in scale")
    const scaleType: ScaleType = isV21 ? byteToScaleType(readByte(bytes, offset++)) : ('chromatic' as ScaleType);

    // Parse per-degree colors if color_mode=1
    let degreeColors: [number, number, number][] | undefined;
    if (colorMode === 1 && parsedTuningTable) {
      degreeColors = [];
      for (let i = 0; i < parsedTuningTable.notes_per_period; i++) {
        degreeColors.push([readByte(bytes, offset++), readByte(bytes, offset++), readByte(bytes, offset++)]);
      }
    }

    parsedScaleColoring = {
      enabled: scaleEnabled,
      root_note: rootDegree,
      root_degree: rootDegree,
      scale_type: scaleType,
      colors: {
        root: rootRGB,
        in_scale: inScaleRGB,
        non_scale: nonScaleRGB,
      },
      ...(colorMode === 1 && degreeColors && {
        color_mode: 'per_degree' as const,
        degree_colors: degreeColors,
      }),
    };
  } else {
    const scaleEnabled = readByte(bytes, offset++) !== 0;
    const scaleRootNote = readByte(bytes, offset++);
    offset++; // brightness
    offset++; // Reserved

    const scaleColors: [number, number, number][] = [];
    for (let i = 0; i < 12; i++) {
      scaleColors.push([readByte(bytes, offset++), readByte(bytes, offset++), readByte(bytes, offset++)]);
    }

    // Detect scale type from 12-color array
    const rootColor = scaleColors[0] ?? [255, 0, 0];
    let detectedScaleType: ScaleType = 'major';
    let inScaleColor: [number, number, number] = [0, 100, 255];
    let nonScaleColor: [number, number, number] = [80, 80, 80];

    const colorsMatch = (a: [number, number, number], b: [number, number, number]) =>
      a[0] === b[0] && a[1] === b[1] && a[2] === b[2];

    let foundInScale = false;
    let foundNonScale = false;
    for (let i = 1; i < 12; i++) {
      const c = scaleColors[i] ?? [0, 0, 0];
      if (!foundInScale && !colorsMatch(c, rootColor)) {
        inScaleColor = c;
        foundInScale = true;
      } else if (foundInScale && !colorsMatch(c, rootColor) && !colorsMatch(c, inScaleColor)) {
        nonScaleColor = c;
        foundNonScale = true;
        break;
      }
    }

    if (foundInScale && !foundNonScale) {
      nonScaleColor = inScaleColor;
    }

    if (foundInScale) {
      const observedMask = [1, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0];
      for (let i = 1; i < 12; i++) {
        const c = scaleColors[i] ?? [0, 0, 0];
        if (colorsMatch(c, rootColor) || colorsMatch(c, inScaleColor)) {
          observedMask[i] = 1;
        }
      }
      for (const [scaleKey, mask] of Object.entries(SCALE_MASKS)) {
        let matches = true;
        for (let i = 0; i < 12; i++) {
          if (observedMask[i] !== mask[i]) { matches = false; break; }
        }
        if (matches) { detectedScaleType = scaleKey as ScaleType; break; }
      }
    }

    // Detect per-degree mode: if more than 3 unique colors exist, it's per-degree
    const uniqueColors = new Set(scaleColors.map(c => `${c[0]},${c[1]},${c[2]}`));
    const isPerDegree = uniqueColors.size > 3;

    parsedScaleColoring = {
      enabled: scaleEnabled,
      root_note: scaleRootNote,
      scale_type: detectedScaleType,
      colors: {
        root: rootColor,
        in_scale: inScaleColor,
        non_scale: nonScaleColor,
      },
      ...(isPerDegree && {
        color_mode: 'per_degree' as const,
        degree_colors: scaleColors as [number, number, number][],
      }),
    };
  }

  // === Parse layers (8 bytes each) ===
  const parsedLayers: Array<{
    id: number;
    name: string;
    default?: boolean;
    note_mapping?: {
      origin: { q: number; r: number };
      base_note: number;
      intervals: { q: number; r: number };
      cents_mode?: boolean;
      interval_q_cents?: number;
      interval_r_cents?: number;
    };
  }> = [];
  const parsedLedLayers: LEDLayerConfig[] = [];
  let defaultLayerCount = 0;
  for (let i = 0; i < layerCount; i++) {
    const layerId = readByte(bytes, offset++);
    const layerFlags = readByte(bytes, offset++);
    const isDefault = (layerFlags & 0x01) !== 0;
    if (isDefault) defaultLayerCount++;

    const baseColorR = readByte(bytes, offset++);
    const baseColorG = readByte(bytes, offset++);
    const baseColorB = readByte(bytes, offset++);
    const layerBrightness = byteToBrightness(readByte(bytes, offset++));
    const layerMode = byteToLedLayerMode(readByte(bytes, offset++));
    offset++;

    parsedLayers.push({
      id: layerId,
      name: `Layer ${layerId}`,
      ...(isDefault && { default: true }),
    });

    const isDefaultLedConfig = (baseColorR === 80 && baseColorG === 80 && baseColorB === 80
                            && Math.abs(layerBrightness - 0.5) < 0.01
                            && layerMode === 'full')
                            || (baseColorR === 0 && baseColorG === 0 && baseColorB === 0
                            && layerBrightness === 0
                            && layerMode === 'off');
    if (!isDefaultLedConfig) {
      parsedLedLayers.push({
        layer: layerId,
        base_color: [baseColorR, baseColorG, baseColorB],
        brightness: layerBrightness,
        mode: layerMode,
      });
    }
  }

  if (defaultLayerCount !== 1) {
    errors.push(`Must have exactly one default layer, found ${defaultLayerCount}`);
  }

  // === Parse zones (8 bytes v1.x, 10 bytes v2.0) ===
  const parsedZones: Zone[] = [];
  for (let i = 0; i < zoneCount; i++) {
    const qMin = view.getInt8(offset++);
    const qMax = view.getInt8(offset++);
    const rMin = view.getInt8(offset++);
    const rMax = view.getInt8(offset++);
    const channel = readByte(bytes, offset++);
    const transpose = view.getInt8(offset++);

    const zone: Zone = {
      name: `Zone ${i}`,
      bounds: {
        q_min: qMin,
        q_max: qMax,
        ...(rMin !== -128 && { r_min: rMin }),
        ...(rMax !== -128 && { r_max: rMax }),
      },
      channel,
      transpose,
    };

    if (isV2) {
      const transposeCents = view.getInt16(offset, true); offset += 2;
      offset += 2; // reserved
      zone.transpose_cents = transposeCents;
    } else {
      offset += 2; // reserved
    }

    parsedZones.push(zone);
  }

  // === Parse layer note mappings (8 bytes v1.x, 12 bytes v2.0) ===
  for (let i = 0; i < layerNoteMappingCount; i++) {
    if (isV2) {
      const layerId = readByte(bytes, offset++);
      const lnmFlags = readByte(bytes, offset++);
      const originQ = view.getInt8(offset++);
      const originR = view.getInt8(offset++);
      const baseNote = readByte(bytes, offset++);
      offset++; // reserved
      const intervalQCents = view.getInt16(offset, true); offset += 2;
      const intervalRCents = view.getInt16(offset, true); offset += 2;
      offset += 2; // reserved

      const layer = parsedLayers.find(l => l.id === layerId);
      if (layer) {
        layer.note_mapping = {
          origin: { q: originQ, r: originR },
          base_note: baseNote,
          intervals: {
            q: Math.round(intervalQCents / 1000),
            r: Math.round(intervalRCents / 1000),
          },
          cents_mode: (lnmFlags & 0x01) !== 0,
          interval_q_cents: intervalQCents,
          interval_r_cents: intervalRCents,
        };
      }
    } else {
      const layerId = readByte(bytes, offset++);
      offset++;
      const originQ = view.getInt8(offset++);
      const originR = view.getInt8(offset++);
      const baseNote = readByte(bytes, offset++);
      const intervalQ = view.getInt8(offset++);
      const intervalR = view.getInt8(offset++);
      offset++;

      const layer = parsedLayers.find(l => l.id === layerId);
      if (layer) {
        layer.note_mapping = {
          origin: { q: originQ, r: originR },
          base_note: baseNote,
          intervals: { q: intervalQ, r: intervalR },
        };
      }
    }
  }

  // === Parse zone note mappings (8 bytes v1.x, 12 bytes v2.0) ===
  for (let i = 0; i < zoneNoteMappingCount; i++) {
    if (isV2) {
      const zoneIndex = readByte(bytes, offset++);
      const znmFlags = readByte(bytes, offset++);
      const originQ = view.getInt8(offset++);
      const originR = view.getInt8(offset++);
      const baseNote = readByte(bytes, offset++);
      offset++; // reserved
      const intervalQCents = view.getInt16(offset, true); offset += 2;
      const intervalRCents = view.getInt16(offset, true); offset += 2;
      offset += 2; // reserved

      const zone = parsedZones[zoneIndex];
      if (zone) {
        zone.note_mapping = {
          origin: { q: originQ, r: originR },
          base_note: baseNote,
          intervals: {
            q: Math.round(intervalQCents / 1000),
            r: Math.round(intervalRCents / 1000),
          },
          cents_mode: (znmFlags & 0x01) !== 0,
          interval_q_cents: intervalQCents,
          interval_r_cents: intervalRCents,
        };
      }
    } else {
      const zoneIndex = readByte(bytes, offset++);
      offset++;
      const originQ = view.getInt8(offset++);
      const originR = view.getInt8(offset++);
      const baseNote = readByte(bytes, offset++);
      const intervalQ = view.getInt8(offset++);
      const intervalR = view.getInt8(offset++);
      offset++;

      const zone = parsedZones[zoneIndex];
      if (zone) {
        zone.note_mapping = {
          origin: { q: originQ, r: originR },
          base_note: baseNote,
          intervals: { q: intervalQ, r: intervalR },
        };
      }
    }
  }

  // === Parse key overrides (16 bytes each) ===
  const parsedOverrides: KeyOverride[] = [];
  for (let i = 0; i < overrideCount; i++) {
    const posQ = view.getInt8(offset++);
    const posR = view.getInt8(offset++);
    const layer = readByte(bytes, offset++);
    const actionTypeValue = readByte(bytes, offset++);
    const actionType = byteToActionType(actionTypeValue);

    const actionDataStart = offset;
    let action: KeyAction;
    switch (actionType) {
      case 'note':
        action = { type: 'note', note: readByte(bytes, offset++), channel: readByte(bytes, offset++) };
        break;
      case 'cc':
        action = { type: 'cc', cc: readByte(bytes, offset++), value: readByte(bytes, offset++), channel: readByte(bytes, offset++) };
        break;
      case 'program_change':
        action = { type: 'program_change', program: readByte(bytes, offset++), channel: readByte(bytes, offset++) };
        break;
      case 'bank_program':
        action = { type: 'bank_program', program: readByte(bytes, offset++), channel: readByte(bytes, offset++), bank_msb: readByte(bytes, offset++), bank_lsb: readByte(bytes, offset++) };
        break;
      case 'chord': {
        const noteCount = readByte(bytes, offset++);
        const ch = readByte(bytes, offset++);
        const notes: number[] = [];
        for (let n = 0; n < MAX_CHORD_NOTES; n++) {
          const note = readByte(bytes, offset++);
          if (n < noteCount && note > 0) notes.push(note);
        }
        action = { type: 'chord', notes, channel: ch };
        break;
      }
      case 'layer_toggle':
        action = { type: 'layer_toggle', target_layer: readByte(bytes, offset++) };
        break;
      case 'layer_momentary':
        action = { type: 'layer_momentary', target_layer: readByte(bytes, offset++) };
        break;
      default:
        action = { type: 'note', note: 60, channel: 0 };
    }
    offset = actionDataStart + 12;
    parsedOverrides.push({ pos: { q: posQ, r: posR }, layer, action });
  }

  // === Parse LED overrides (12 bytes each) ===
  const parsedLEDOverrides: LEDOverride[] = [];
  for (let i = 0; i < ledOverrideCount; i++) {
    const posQ = view.getInt8(offset++);
    const posR = view.getInt8(offset++);
    const layer = readByte(bytes, offset++);
    const ovFlags = readByte(bytes, offset++);
    const hasPressed = (ovFlags & 0x01) !== 0;

    const idleR2 = readByte(bytes, offset++);
    const idleG2 = readByte(bytes, offset++);
    const idleB2 = readByte(bytes, offset++);
    const idleBright = byteToBrightness(readByte(bytes, offset++));

    const pressedR2 = readByte(bytes, offset++);
    const pressedG2 = readByte(bytes, offset++);
    const pressedB2 = readByte(bytes, offset++);
    const pressedBright = byteToBrightness(readByte(bytes, offset++));

    const ledOverride: LEDOverride = {
      pos: { q: posQ, r: posR },
      layer,
      idle: { color: [idleR2, idleG2, idleB2], brightness: idleBright },
    };
    if (hasPressed) {
      ledOverride.pressed = { color: [pressedR2, pressedG2, pressedB2], brightness: pressedBright };
    }
    parsedLEDOverrides.push(ledOverride);
  }

  // === Optional: Config name (32 bytes, v2.1+ when HAS_NAME flag set) ===
  let parsedConfigName: string | undefined;
  if (hasName) {
    const nameBytes = bytes.slice(offset, offset + CONFIG_NAME_SIZE);
    offset += CONFIG_NAME_SIZE;
    // Find null terminator (first 0 byte) and decode UTF-8
    let nameLen = 0;
    while (nameLen < nameBytes.length && nameBytes[nameLen] !== 0) nameLen++;
    parsedConfigName = new TextDecoder('utf-8').decode(nameBytes.slice(0, nameLen));
  }

  if (errors.length > 0) {
    return { valid: false, errors };
  }

  // Build config store object
  const config: Partial<ConfigStore> = {
    columns,
    rows,
    keysPerModule,
    moduleRowShift: parsedModuleRowShift,
    noteMapping: parsedNoteMapping,
    tuningEnabled: parsedTuningEnabled,
    ...(parsedTuningTable && { tuningTable: parsedTuningTable }),
    ...(parsedConfigName && { configName: parsedConfigName }),
    ledSettings: {
      enabled: ledEnabledSetting && ledEnabledFlag,
      brightness,
      perceptual_profile: perceptualProfile,
      update_rate_hz: updateRateHz,
      fade_default_ms: fadeDefaultMs,
    },
    ledDefaultBehavior: {
      idle: { color: [idleR, idleG, idleB], brightness: idleBrightness },
      pressed: { color: [pressedR, pressedG, pressedB], brightness: pressedBrightness, fade_ms: pressedFadeMs },
    },
    scaleColoring: parsedScaleColoring,
    layers: parsedLayers,
    ledLayers: parsedLedLayers,
    zones: parsedZones,
    overrides: parsedOverrides,
    ledOverrides: parsedLEDOverrides,
    ...(parsedAnalogConfig && { analogConfig: parsedAnalogConfig }),
  };

  return { valid: true, config };
}

/**
 * Read binary file from File object
 *
 * @param file - File object from file input
 * @returns Promise resolving to ArrayBuffer
 */
export async function readBinaryFile(file: File): Promise<ArrayBuffer> {
  return file.arrayBuffer();
}
